import { memo, useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { ArrowLeftIcon, ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { ReactComponent as Logo } from "@sablier/v2-assets/logo/icon-gradient.svg";
import { External } from "@sablier/v2-components/atoms";
import { Icon } from "@sablier/v2-components/molecules";
import { Modal as Base } from "@sablier/v2-components/organisms";
import { DEFAULT_TOKEN_LIST } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { IToken } from "@sablier/v2-types";
import { useModalTokenPicker } from "~/client/hooks";
import Home from "./Home";
import Manage from "./Manage";
import Registry from "./Registry";
import Footer from "./common/Footer";

const Modal = styled(Base.Card)`
  div[data-component="modal-card"] {
    --background-modal: ${(props) => props.theme.colors.dark200};
    height: min(80vh, 800px);
    max-width: 600px;
  }
  ${(props) => props.theme.medias.maxSM} {
    div[data-component="modal-card"] {
      height: calc(100vh - ${(props) => props.theme.sizes.edge} * 1);
    }
  }
`;

const CuratedPartial = styled(External)`
  ${(props) => props.theme.styles.row}
  & {
    position: relative;
    min-height: 40px;
    cursor: pointer;
    & > *:last-child {
      color: ${(props) => props.theme.colors.primaryMiddle};
    }
  }
`;

const CuratedLogo = styled.div`
  width: 14px;
  height: 18px;
  & > svg {
    width: 100%;
    height: 100%;
  }
`;

const CuratedTitle = styled.div`
  margin-right: 6px;
  margin-left: 8px;
  & > p {
    ${(props) => props.theme.styles.textButton}
    ${(props) => props.theme.styles.textGradientPrimary}
  }
`;

const Before = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    padding-right: ${(props) => props.theme.sizes.edge};
  }
`;

const Back = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    flex-shrink: 0;
    justify-content: center;
    width: 40px;
    height: 40px;
    color: ${(props) => props.theme.colors.gray400};
    border-radius: 50%;
    background-color: ${(props) => props.theme.colors.dark400};
    cursor: pointer;

    &:hover,
    &:active {
      background-color: ${(props) => props.theme.colors.dark600};
    }
  }
`;

const Curated = styled(CuratedPartial)`
  ${(props) => props.theme.medias.maxSM} {
    gap: 8px;
    ${CuratedTitle} {
      display: none;
    }
  }
`;

enum TokenPickerPage {
  HOME,
  MANAGE,
  REGISTRY,
}

const AfterTitle = memo(function AfterTitle() {
  return (
    <Curated to={DEFAULT_TOKEN_LIST.homepage}>
      <CuratedLogo>
        <Logo />
      </CuratedLogo>
      <CuratedTitle>
        <p>Community list</p>
      </CuratedTitle>
      <Icon value={ArrowUpRightIcon} size={16} />
    </Curated>
  );
});

const BeforeTitle = memo(function BeforeTitle({
  onBack,
}: {
  onBack: () => void;
}) {
  return (
    <Before>
      <Back onClick={onBack}>
        <Icon
          purpose={"heroicon"}
          value={ArrowLeftIcon}
          size={18}
          thickness={2.6}
        />
      </Back>
    </Before>
  );
});

function TokenPicker() {
  const { id, data, doClose } = useModalTokenPicker();
  const [page, setPage] = useState<TokenPickerPage>(TokenPickerPage.HOME);
  const { t } = useT();

  const onChoose = useCallback(
    (token: IToken) => {
      if (data && _.isFunction(data.onChoose)) {
        data.onChoose(token);
      }
      doClose();
    },
    [data, doClose],
  );

  const onGoTo = useCallback(
    (page: TokenPickerPage) => {
      setPage(page);
    },
    [setPage],
  );

  const onHome = useCallback(() => onGoTo(TokenPickerPage.HOME), [onGoTo]);
  const onMange = useCallback(() => onGoTo(TokenPickerPage.MANAGE), [onGoTo]);
  const onRegistry = useCallback(
    () => onGoTo(TokenPickerPage.REGISTRY),
    [onGoTo],
  );

  const title = useMemo(() => {
    if (page === TokenPickerPage.REGISTRY) {
      return t("modalCards.token.search");
    }
    if (page === TokenPickerPage.MANAGE) {
      return t("modalCards.token.manage");
    }
    return t("modalCards.token.search");
  }, [page, t]);

  return (
    <Modal
      id={id}
      afterTitle={<AfterTitle />}
      beforeTitle={
        page !== TokenPickerPage.HOME && <BeforeTitle onBack={onHome} />
      }
      isBackdropDisabled={false}
      isCloseDisabled
      title={title}
      elevation={1000}
    >
      {page === TokenPickerPage.HOME && (
        <Home onRegistry={onRegistry} onChoose={onChoose} />
      )}
      {page === TokenPickerPage.REGISTRY && <Registry onHome={onHome} />}
      {page === TokenPickerPage.MANAGE && (
        <Manage onHome={onHome} onRegistry={onRegistry} />
      )}
      <Footer
        isManageShown={page !== TokenPickerPage.MANAGE}
        isRegistryShown={page === TokenPickerPage.HOME}
        onManage={onMange}
        onRegistry={onRegistry}
      />
    </Modal>
  );
}

export default TokenPicker;
